import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { apiFetch } from "@/lib/api";
import { colors } from "@/theme/tokens";

// Standalone, unauthenticated page: opened cold from a Telegram link, no
// lattice login at all — the token in the URL IS the credential (same
// trust model as a password-reset link). Mounted OUTSIDE the normal
// authed app shell in App.tsx, before the auth-store gate even runs.
//
// Shows exactly ONE browser tab belonging to the agent that needs a
// human to sign in — never the agent's whole desktop, which would leak
// whatever else it's logged into. The canvas is a live JPEG screencast
// relayed over CDP by the backend (services/cdp_relay.py); taps/typing
// relay back the same way. Deliberately tap-to-click only (no drag) —
// this exists to get through a login form, not to browse.

type Phase = "loading" | "invalid" | "connecting" | "live" | "ended" | "error";

interface StatusResponse {
  status: "pending" | "completed" | "cancelled" | "expired" | "not_found";
  url?: string | null;
  reason?: string | null;
}

function wsUrl(token: string): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/api/login-assist/${encodeURIComponent(token)}/ws`;
}

function tokenFromPath(): string {
  const parts = window.location.pathname.split("/").filter(Boolean);
  // /login-assist/<token>
  return parts[1] ?? "";
}

const INVALID_COPY: Record<string, { title: string; body: string }> = {
  completed: {
    title: "Already signed in",
    body: "This link was already used to finish signing in, so you're done here.",
  },
  cancelled: {
    title: "Link cancelled",
    body: "This sign-in request was cancelled. Ask the agent to send a new link if you still need to sign in.",
  },
  expired: {
    title: "This link has expired",
    body: "Sign-in links are single-use and time out after a few minutes. Ask the agent to send a new one.",
  },
  not_found: {
    title: "Link not recognised",
    body: "Check you copied the whole link, or ask the agent to send a new one.",
  },
};

export function LoginAssistPage() {
  const [phase, setPhase] = useState<Phase>("loading");
  const [message, setMessage] = useState<string>("");
  const [reason, setReason] = useState<string | null>(null);
  const [siteUrl, setSiteUrl] = useState<string | null>(null);

  const token = useRef(tokenFromPath()).current;
  const wsRef = useRef<WebSocket | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);
  // State, not a ref: the container's aspect-ratio CSS must re-render
  // whenever this changes. Seeded from the initial CDP viewport report, but
  // NOT trusted forever — see the frame handler below.
  const [viewport, setViewport] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const lastPointer = useRef<{ x: number; y: number } | null>(null);
  const pendingImage = useRef<HTMLImageElement | null>(null);

  const send = useCallback((payload: unknown) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
    }
  }, []);

  const finish = useCallback(
    async (status: "completed" | "cancelled") => {
      wsRef.current?.close();
      try {
        await apiFetch(`/api/login-assist/${encodeURIComponent(token)}/complete`, {
          method: "POST",
          body: { status },
        });
      } catch {
        // Best-effort — the WS close already ends the human's view; a
        // failed status POST here just means the agent's `wait` call
        // times out naturally instead of getting an instant answer.
      }
      setPhase("ended");
      setMessage(
        status === "completed"
          ? "Thanks — the agent will pick up right where it left off."
          : "No problem — the agent has been told you're not signing in.",
      );
    },
    [token],
  );

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setPhase("invalid");
      setMessage(INVALID_COPY.not_found.body);
      return;
    }

    (async () => {
      let initial: StatusResponse;
      try {
        initial = await apiFetch<StatusResponse>(`/api/login-assist/${encodeURIComponent(token)}`);
      } catch {
        if (cancelled) return;
        setPhase("error");
        setMessage("Couldn't reach the server. Check your connection and reload.");
        return;
      }
      if (cancelled) return;
      if (initial.status !== "pending") {
        const copy = INVALID_COPY[initial.status] ?? INVALID_COPY.not_found;
        setPhase("invalid");
        setMessage(copy.body);
        return;
      }
      setReason(initial.reason ?? null);
      setSiteUrl(initial.url ?? null);
      setPhase("connecting");

      const ws = new WebSocket(wsUrl(token));
      wsRef.current = ws;

      ws.onmessage = (evt) => {
        let parsed: Record<string, unknown>;
        try {
          parsed = JSON.parse(evt.data as string);
        } catch {
          return;
        }
        if (parsed.type === "init") {
          // First-paint sizing only — a best guess so the canvas isn't 0x0
          // before the first frame arrives. NOT trusted after that: the
          // frame handler below re-syncs to the REAL decoded image on every
          // single frame, because the reported CSS viewport at connect time
          // can go stale (the agent's browser window resizing, a different
          // display mode, or another session touching the same window
          // produced exactly this — a live target whose actual dimensions
          // silently diverged from what we cached once at the start,
          // stretching every frame after that into the wrong ratio).
          const w = Number(parsed.width) || 1280;
          const h = Number(parsed.height) || 800;
          setViewport({ w, h });
          const canvas = canvasRef.current;
          if (canvas) {
            canvas.width = w;
            canvas.height = h;
          }
          setPhase("live");
        } else if (parsed.type === "frame" && typeof parsed.data === "string") {
          const canvas = canvasRef.current;
          if (!canvas) return;
          const img = pendingImage.current ?? new Image();
          pendingImage.current = img;
          img.onload = () => {
            // Re-sync to the DECODED image's real pixel size every frame —
            // this is the actual source of truth, not the one-time init
            // guess. Cheap: a no-op comparison on the (usual) frame where
            // nothing changed; only resizes the canvas + updates the
            // aspect-ratio state on the frame where it actually did.
            if (canvas.width !== img.naturalWidth || canvas.height !== img.naturalHeight) {
              canvas.width = img.naturalWidth;
              canvas.height = img.naturalHeight;
              setViewport({ w: img.naturalWidth, h: img.naturalHeight });
            }
            const ctx = canvas.getContext("2d");
            ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
          };
          img.src = `data:image/jpeg;base64,${parsed.data}`;
        } else if (parsed.type === "error") {
          setPhase("error");
          setMessage(
            typeof parsed.message === "string"
              ? parsed.message
              : "Lost the connection to the agent's browser tab.",
          );
        }
      };
      ws.onclose = () => {
        // A close the human didn't initiate (finish() already set "ended"
        // before closing) means the tab or the connection dropped.
        setPhase((p) => (p === "live" || p === "connecting" ? "error" : p));
        setMessage((m) => m || "The connection closed. Ask the agent for a new link if you're not done.");
      };
    })();

    return () => {
      cancelled = true;
      wsRef.current?.close();
    };
    // token is stable for the page's lifetime (read once from the URL).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toCanvasCoords = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const p = toCanvasCoords(e);
      if (!p) return;
      lastPointer.current = p;
      send({ type: "mouse", event: "down", x: p.x, y: p.y });
      // Any tap might be a text field — bring up the keyboard. A tap on
      // a non-field element just shows the keyboard briefly; harmless.
      hiddenInputRef.current?.focus();
    },
    [send, toCanvasCoords],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const p = toCanvasCoords(e) ?? lastPointer.current;
      if (!p) return;
      send({ type: "mouse", event: "up", x: p.x, y: p.y });
    },
    [send, toCanvasCoords],
  );

  const onHiddenInput = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const native = e.nativeEvent as InputEvent;
      if (native.inputType?.startsWith("insert") && native.data) {
        send({ type: "text", text: native.data });
      }
      e.currentTarget.value = "";
    },
    [send],
  );

  const onHiddenKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === "Backspace" || e.key === "Tab") {
        send({ type: "key", key: e.key });
        e.preventDefault();
      }
    },
    [send],
  );

  return (
    <div
      className="flex app-vh w-full flex-col items-center overflow-y-auto pt-safe pb-safe"
      style={{ background: colors.bg }}
    >
      {/* Inner padding: pt-safe / pb-safe replace the outer box's own. */}
      <div className="flex w-full max-w-[552px] flex-col items-center gap-4 px-4 py-6">
        <p className="text-center text-xs text-fg-muted">Corvus · Sign-in help</p>

        {phase === "loading" && <p className="mt-10 text-sm text-fg-muted">Loading…</p>}

        {phase === "invalid" && (
          <TerminalCard title="This link isn't available" body={message} />
        )}

        {phase === "error" && (
          <TerminalCard title="Something went wrong" body={message} tone="danger" />
        )}

        {phase === "ended" && <TerminalCard title="All done" body={message} tone="success" />}

        {(phase === "connecting" || phase === "live") && (
          <>
            <div className="min-w-0 max-w-full text-center">
              <h1 className="text-md font-semibold text-fg [text-wrap:balance]">{reason ? reason : "Sign in to continue"}</h1>
              {siteUrl && <p className="mt-1 max-w-[440px] truncate text-xs text-fg-subtle">{siteUrl}</p>}
            </div>

            <div
              className="relative w-full overflow-hidden rounded-card border border-line-subtle bg-panel"
              style={{
                aspectRatio: viewport.w && viewport.h
                  ? `${viewport.w} / ${viewport.h}`
                  : "4 / 3",
              }}
            >
              {phase === "connecting" && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-sm text-fg-muted">Connecting…</p>
                </div>
              )}
              <canvas
                ref={canvasRef}
                onPointerDown={onPointerDown}
                onPointerUp={onPointerUp}
                className="w-full h-full touch-none select-none"
                style={{ display: phase === "live" ? "block" : "none" }}
              />
              {/* Off-screen relay target for the phone's native keyboard —
                  invisible but focusable (display:none would not accept
                  focus / trigger the keyboard on iOS Safari). */}
              <input
                ref={hiddenInputRef}
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                onInput={onHiddenInput}
                onKeyDown={onHiddenKeyDown}
                className="absolute opacity-0"
                style={{ top: -1000, left: -1000, width: 1, height: 1 }}
                aria-hidden="true"
                tabIndex={-1}
              />
            </div>

            <p className="text-center text-sm text-fg-muted [text-wrap:pretty]">
              Tap to use the page like a normal browser, and pinch to zoom in on small fields. Only this one tab is
              shown here, nothing else the agent has open.
            </p>

            <div className="mt-2 flex w-full gap-3">
              <Button type="button" variant="secondary" size="lg" className="h-11 flex-1" onClick={() => void finish("cancelled")}>
                Cancel
              </Button>
              <Button
                type="button"
                size="lg"
                className="h-11 flex-1"
                onClick={() => void finish("completed")}
                disabled={phase !== "live"}
              >
                I&apos;m signed in
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TerminalCard({
  title,
  body,
  tone = "neutral",
}: {
  title: string;
  body: string;
  tone?: "neutral" | "danger" | "success";
}) {
  const titleTone = tone === "danger" ? "text-danger" : tone === "success" ? "text-success" : "text-fg";
  return (
    <div className="mt-10 flex w-full flex-col items-center gap-2 text-center">
      <h1 className={`text-md font-semibold ${titleTone}`}>{title}</h1>
      <p className="max-w-[380px] text-sm text-fg-muted [text-wrap:pretty]">{body}</p>
    </div>
  );
}
